import { DragDropProvider } from '@dnd-kit/react'
import type { DragEndEvent } from '@dnd-kit/react'
import type { TestGroupingGroup, TestGroupingItem } from '@vedalib/editor/lib/elements'
import { rtValueToText } from '@vedalib/rich-text'
import lodash from 'lodash'
import { useRef } from 'react'
import { useContext } from 'use-context-selector'

import { PreviewMode } from 'services/Store/Project/enums'
import { t } from 'services/Translation'
import { createDndAccessibilityPlugin, focusFirstFocusableInElement } from 'utils/accessibility'

import TestGroupingContext from '../../TestGroupingContext'
import { parseStackDraggableId } from '../../helper'
import Result from '../Result/Result'
import AnswerStack from './AnswerStack'
import * as s from './DefaultElement.module.scss'
import GroupCard from './GroupCard'

const isGroup = (entity: TestGroupingItem | TestGroupingGroup): entity is TestGroupingGroup => {
  return (entity as TestGroupingGroup).target !== undefined
}

const getName = (entity: TestGroupingItem | TestGroupingGroup | null | undefined): string | undefined => {
  if (!entity) {
    return undefined
  }
  if (isGroup(entity)) {
    return rtValueToText(entity.target.label) || entity.target.image?.accessibility || entity.value
  }
  return rtValueToText(entity.label) || entity?.image?.accessibility || entity?.value
}

const DefaultElement = () => {
  const {
    styles,
    shuffledAnswers,
    validationMap,
    destination,
    value,
    blockResult,
    mode,
    elementId,
    setDestination,
    setShuffledAnswers,
    setTestResult,
    onClickOutside,
    entityAnalyticsConfig,
    sendXapiData,
    baseObjectExtensions,
  } = useContext(TestGroupingContext)
  const items = value.items
  const answers = items.flatMap((group) => group?.items || [])
  const isEditor = mode.previewMode === PreviewMode.editor
  const containerRef = useRef<HTMLDivElement>(null)

  const onDragEnd = ({ operation, canceled }: Parameters<DragEndEvent>[0]) => {
    setTimeout(() => {
      if (containerRef.current) {
        focusFirstFocusableInElement(containerRef.current)
      }
    }, 100)

    if (canceled || !operation.source || !operation.target) {
      return
    }

    const sourceId = String(operation.source.id)
    const destGroupId = String(operation.target.id)
    const { isFromStack, sourceGroupValue, itemValue } = parseStackDraggableId(sourceId)

    if (isFromStack && sourceGroupValue === destGroupId) {
      return
    }

    let draggedItem: TestGroupingItem | undefined
    if (isFromStack) {
      draggedItem = destination
        .find((g) => g.value === sourceGroupValue)
        ?.items?.find((i) => i.value === itemValue)
    } else {
      draggedItem = shuffledAnswers.find((a) => a.value === sourceId)
    }

    if (!draggedItem) {
      return
    }

    setTestResult((prev) => ({
      ...prev,
      [draggedItem!.value]: validationMap[draggedItem!.value] === destGroupId,
    }))

    setDestination((prev) =>
      prev.map((group) => ({
        ...group,
        items:
          group.value === sourceGroupValue
            ? group.items?.filter((i) => i.value !== draggedItem!.value)
            : group.value === destGroupId
              ? [...(group.items || []), draggedItem!]
              : group.items,
      })),
    )

    if (!isFromStack) {
      setShuffledAnswers((prev) => prev.filter((e) => e.value !== draggedItem!.value))
    }

    const dropGroup = destination.find((g) => g.value === destGroupId)
    const dragIndex = isFromStack
      ? (destination.find((g) => g.value === sourceGroupValue)?.items?.findIndex((i) => i.value === draggedItem!.value) ?? -1)
      : shuffledAnswers.findIndex((a) => a.value === draggedItem!.value)
    const data = entityAnalyticsConfig.interacted({
      resultExtensions: {
        dragItem: draggedItem.value,
        dropGroup: destGroupId,
        labelDragItem: rtValueToText(draggedItem.label),
        labelDropGroup: rtValueToText(dropGroup?.target?.label),
        dragIndex,
        dropGroupIndex: destination.findIndex((g) => g.value === destGroupId),
      },
      objectExtensions: baseObjectExtensions,
      elementId,
    })
    sendXapiData(data)
  }

  return (
    <DragDropProvider
      onDragEnd={onDragEnd}
      plugins={(defaults) => [...defaults, createDndAccessibilityPlugin(getName)]}
    >
      <div
        className={s.container}
        onClick={onClickOutside}
        ref={containerRef}
        style={{
          flexDirection:
            styles.answer.position === ('bottom' as string) ? 'column-reverse' : 'column',
          gap: styles.indents.marginTop,
        }}
        tabIndex={-1}
      >
        <div className={s.row}>
          {!!answers.length && (
            <div
              style={{
                height: styles.common.height,
              }}
            >
              {shuffledAnswers.length === 0 && (!!blockResult || isEditor) && (
                <div style={{ display: value.showResult ? 'block' : 'none' }}>
                  <Result />
                </div>
              )}
              {shuffledAnswers.length > 0 && <AnswerStack answers={shuffledAnswers} />}
            </div>
          )}
          {!answers.length && (
            <div className={s.emptyAnswer} style={{ ...lodash.omit(styles.common, ['color']) }}>
              {t('elements.testGrouping.emptyAnswer')}
            </div>
          )}
        </div>
        <div className={s.destContainer} style={{ gap: styles.indents.gap }}>
          {destination.map((e) => (
            <GroupCard group={e} key={e.value} />
          ))}
        </div>
      </div>
    </DragDropProvider>
  )
}

export default DefaultElement
